import {Portal} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {useCallback, useRef} from 'react'
import {CreateIssue, type CreateIssueProps} from '../CreateIssue'
import {useIssueCreateConfigContext} from '../contexts/IssueCreateConfigContext'
import {useSafeClose} from '../hooks/use-safe-close'
import {CreateIssueFooter} from '../CreateIssueFooter'
import {DisplayMode} from '../utils/display-mode'
import type {OnCreateProps} from '../CreateIssueForm'
import type {IssueCreateUrlParams} from '../utils/template-args'
import {CreateIssueDialogHeader} from './CreateIssueDialogHeader'

export type CreateIssueDialogProps = {
  navigate: (url: string) => void
} & CreateIssueProps

export const CreateIssueDialog = ({
  onCreateSuccess,
  onCancel,
  navigate,
  ...props
}: CreateIssueDialogProps): JSX.Element => {
  const {optionConfig, displayMode, resetDisplayMode} = useIssueCreateConfigContext()

  // These two callbacks wrap the passed in callbacks to ensure that the display mode is reset
  // The reason why it's done here is we have access to the create context safely.
  const resetDisplayModeOnClose = useCallback(() => {
    onCancel()
    resetDisplayMode()
  }, [onCancel, resetDisplayMode])

  const resetDisplayModeOnCreateSuccess = useCallback(
    ({issue, createMore}: OnCreateProps) => {
      onCreateSuccess({issue, createMore})
      if (!createMore) {
        resetDisplayMode()
      }
    },
    [onCreateSuccess, resetDisplayMode],
  )

  const {issueFormRef} = useIssueCreateConfigContext()
  const {onSafeClose} = useSafeClose({
    storageKeyPrefix: optionConfig.storageKeyPrefix,
    issueFormRef,
    onCancel: resetDisplayModeOnClose,
  })

  const createIssueUrlParamsRef = useRef<IssueCreateUrlParams>(null)

  return (
    <Portal>
      <Dialog
        renderHeader={headerProps => (
          <CreateIssueDialogHeader
            createIssueUrlParamsRef={createIssueUrlParamsRef}
            navigate={navigate}
            {...headerProps}
          />
        )}
        renderFooter={() => (
          <Dialog.Footer sx={{display: displayMode === DisplayMode.IssueCreation ? 'flex' : 'none'}}>
            <CreateIssueFooter onClose={() => onSafeClose()} />
          </Dialog.Footer>
        )}
        onClose={onSafeClose}
        width="xlarge"
        height="auto"
        sx={{
          width: '100%',
          margin: 4,
          maxWidth: '800px',
          maxHeight: 'clamp(300px, 80vh, 800px)',
        }}
      >
        <CreateIssue
          onCreateSuccess={resetDisplayModeOnCreateSuccess}
          onCancel={onSafeClose}
          createIssueUrlParamsRef={createIssueUrlParamsRef}
          {...props}
        />
      </Dialog>
    </Portal>
  )
}

try{ CreateIssueDialog.displayName ||= 'CreateIssueDialog' } catch {}